import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, MessageCircle, Tag } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const WHATSAPP_NUMBER = '21696050699';

const ClientMystereTarifs = () => {
  const { t } = useLanguage();

  const plans = [
    {
      id: 'S',
      priceEur: 69,
      priceTnd: 235,
      minVisits: 5,
      popular: false,
      discount: null,
    },
    {
      id: 'M',
      priceEur: 59,
      priceTnd: 200,
      minVisits: 10,
      popular: false,
      discount: null,
    },
    {
      id: 'L',
      priceEur: 54,
      priceTnd: 185,
      minVisits: 20,
      popular: false,
      discount: null,
    },
    {
      id: 'XL',
      priceEur: 49,
      priceTnd: 165,
      minVisits: 40,
      popular: true,
      discount: '-30%',
    },
  ];

  const getWhatsAppUrl = (plan) => {
    const msg = encodeURIComponent(
      `Bonjour Excelsium Conseils,\nJe suis intéressé(e) par le Pack Client Mystère - Formule ${plan.id} (${plan.priceEur}€ / ${plan.priceTnd} TND par visite, ${plan.minVisits} visites minimum).\nMerci de me recontacter.`
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
  };

  return (
    <div data-testid="cm-tarifs-page">
      {/* Hero */}
      <section className="page-header page-header-professionnels" data-testid="cm-tarifs-header">
        <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans animate-fade-in">{t('cm.tagline')}</p>
        <h1 className="font-serif text-4xl md:text-6xl text-white mb-6 animate-fade-in-delay-1">
          {t('cmTarifs.heroTitle')}
        </h1>
        <div className="gold-line gold-line-center mb-6 animate-fade-in-delay-2"></div>
        <p className="text-white/80 text-lg max-w-2xl text-center animate-fade-in-delay-3">
          {t('cmTarifs.heroSubtitle')}
        </p>
      </section>

      {/* Back link */}
      <div className="container-luxury pt-8">
        <Link to="/professionnels/client-mystere" className="inline-flex items-center gap-2 text-[#C9A227] hover:text-[#b08d1f] transition-colors text-sm" data-testid="cm-tarifs-back">
          <ArrowLeft className="w-4 h-4" />
          {t('cmTarifs.backToExplanation')}
        </Link>
      </div>

      {/* Pricing Cards */}
      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="cm-pricing-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('cmTarifs.pricingTag')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">{t('cmTarifs.pricingTitle')}</h2>
            <div className="gold-line gold-line-center"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white border ${plan.popular ? 'border-[#C9A227] shadow-lg' : 'border-[#F5EFD3]'} flex flex-col transition-all duration-300 hover:border-[#C9A227] hover:shadow-md`}
                data-testid={`cm-plan-${plan.id.toLowerCase()}`}
              >
                {plan.discount && (
                  <div className="absolute -top-3 right-4 bg-[#C9A227] text-white text-xs font-bold px-3 py-1.5 flex items-center gap-1" data-testid="cm-discount-badge">
                    <Tag className="w-3 h-3" />
                    {plan.discount}
                  </div>
                )}
                <div className="p-8 text-center flex-1 flex flex-col">
                  <h3 className="font-serif text-4xl text-[#1A1A1A] mb-2">{plan.id}</h3>
                  <p className="text-sm text-gray-500 uppercase tracking-wider mb-6">{t('cmTarifs.allInclusive')}</p>
                  
                  <div className="mb-2">
                    <span className="font-serif text-5xl text-[#C9A227]">{plan.priceEur}</span>
                    <span className="text-[#C9A227] text-lg ml-1">{t('cmTarifs.eurPerVisit')}</span>
                    <span className="text-[#C9A227]/60 text-xs ml-1 uppercase">HT</span>
                  </div>
                  <div className="mb-6">
                    <span className="text-gray-500 text-lg">{plan.priceTnd} TND</span>
                    <span className="text-gray-400 text-sm ml-1">/ {t('cmTarifs.visit')}</span>
                    <span className="text-gray-400 text-xs ml-1 uppercase">HT</span>
                  </div>
                  
                  <div className="p-3 bg-[#FDFCF8] border border-[#F5EFD3] mb-6">
                    <p className="text-[#1A1A1A] font-medium">{plan.minVisits} {t('cmTarifs.visitsMin')}</p>
                  </div>

                  <ul className="space-y-3 mb-8 text-left flex-1">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{t('cmTarifs.feat1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{t('cmTarifs.feat2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{t('cmTarifs.feat3')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{t('cmTarifs.feat4')}</span>
                    </li>
                  </ul>

                  <a
                    href={getWhatsAppUrl(plan)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full inline-flex items-center justify-center gap-2 py-3 px-6 text-sm uppercase tracking-wider transition-all duration-300 ${
                      plan.popular
                        ? 'bg-[#C9A227] text-white hover:bg-[#b08d1f]'
                        : 'border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white'
                    }`}
                    data-testid={`cm-order-${plan.id.toLowerCase()}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    {t('cmTarifs.order')}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Online discount banner */}
          <div className="mt-10 bg-[#1A1A1A] border border-[#C9A227]/30 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6" data-testid="cm-online-discount-banner">
            <div className="flex-shrink-0 bg-[#C9A227] text-white font-serif text-3xl md:text-4xl font-bold px-6 py-3">
              -30%
            </div>
            <div className="text-center md:text-left">
              <p className="text-white font-serif text-xl md:text-2xl mb-1">{t('cmTarifs.onlineDiscountTitle')}</p>
              <p className="text-white/60 text-sm md:text-base">{t('cmTarifs.onlineDiscountDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* What's included reminder */}
      <section className="section-luxury bg-white" data-testid="cm-tarifs-included">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('cmTarifs.includedTag')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">{t('cmTarifs.includedTitle')}</h2>
            <div className="gold-line gold-line-center mb-10"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="flex items-start gap-3 p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                <CheckCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{t('cmTarifs.inc1')}</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                <CheckCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{t('cmTarifs.inc2')}</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                <CheckCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{t('cmTarifs.inc3')}</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                <CheckCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{t('cmTarifs.inc4')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-luxury bg-[#1A1A1A]" data-testid="cm-tarifs-cta">
        <div className="container-luxury text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t('cmTarifs.ctaTitle')}</h2>
          <div className="gold-line gold-line-center mb-8"></div>
          <p className="text-white/70 text-lg mb-10">{t('cmTarifs.ctaText')}</p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="cm-tarifs-contact-cta">
            {t('cmTarifs.contactUs')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ClientMystereTarifs;
